import Component from '../../classes/Component';
import {$dom} from '../../helpers/dom';
import {$events} from '../../helpers/events';
import variables from '../../variables';
import FormValidator from '@yaireo/validator'
import {isElement} from '../../helpers/_utilities';
import {gsap} from 'gsap'
import is from 'is_js';
import intlTelInput from '../../plugins/intl-tel-input/intlTelInputWithUtils';

const {
    callAll,
    attr,
    get,
    remove,
    addClass,
    removeClass,
    exist
} = $dom;

const {formValidationMessages, classNames, customEventNames, selectors} = variables;

export const validationGroupClassName = 'js-group-validate';
export const validationFieldClassName = 'js-field-validate';
export const notifyClassName = 'form__group-notify';

const
    formSelector = '.js-form-validate',
    formGroupSelector = `.${validationGroupClassName}`,
    formFieldSelector = `.${validationFieldClassName}`,
    needValidateClassName = 'is-need-validate',
    recaptchaSelector = selectors.recaptcha,
    phoneExceptionsAttr = 'data-exceptions'
;

const recaptchaKeyAttr = 'data-sitekey';

export default function() {
    const $Page = this;

    return new Component({
        name: 'formValidation',
        requiredTargets: [formSelector, formGroupSelector, formFieldSelector],
        onCreate() {
            this.validator = null;

            this.separateFields = fields => {
                return fields.reduce((acc, target) => {

                    const isValidation = is.object(target) && target.hasOwnProperty('field');
                    const field = isValidation ? target.field : target

                    acc[$dom.hasClass(field, classNames.intlTelInput) ? 'phone' : 'other']
                        .push(isValidation ? target : field)

                    return acc
                }, {
                    phone: [],
                    other: []
                })
            };

            this.savePhoneExceptions = () => {
                if ($dom.exist(formFieldSelector)) {
                    const {phone: phoneFields} = this.separateFields($dom.getAll(formFieldSelector));

                    phoneFields.length > 0 && phoneFields.forEach(field => {
                        const exceptionsAttr = field.getAttribute(phoneExceptionsAttr);

                        if (exceptionsAttr) {
                            field.phoneExceptions = JSON.parse(exceptionsAttr).map(n =>'+'+n);
                            field.removeAttribute(phoneExceptionsAttr)
                        }
                    })
                }
            }

            this.savePhoneExceptions();
        },
        onInit() {
            this.validator = new FormValidator({
                classes: {
                    item: formGroupSelector.replace('.', ''),
                    bad: classNames.invalid,
                    alert: notifyClassName
                },
                texts: formValidationMessages
            });
            this.addedForms = [];

            attr(formSelector, 'novalidate', '');

            const exceptionsIsExist = field => Boolean(field.phoneExceptions) && is.array(field.phoneExceptions) && field.phoneExceptions.length > 0;

            const checkIntlTelInput = field => {
                const {valid: inputIsValid} = this.validatePhoneInput(field);

                const group = field.closest(formGroupSelector);
                const isMessageOutExist = $dom.exist('.'+notifyClassName, group);

                if (inputIsValid) {
                    if (isMessageOutExist)
                        $dom.remove($dom.get('.'+notifyClassName, group))
                } else {
                    let messageOutEl;

                    if (!isMessageOutExist) {
                        messageOutEl = $dom.createElement('div', {class: notifyClassName})
                        group.appendChild(messageOutEl);
                    } else {
                        messageOutEl = $dom.get('.'+notifyClassName, group)
                    }

                    messageOutEl && $dom.text(messageOutEl, variables.formValidationMessages.complete);
                }
            };

            const handleRecaptchaSuccess = (recaptchaRootEl, response) => {

                if (response) {
                    const validatedForm = $dom.getParent(recaptchaRootEl, formSelector);
                    const submitBtn = $dom.get('button[type="submit"]', validatedForm);

                    if (validatedForm) {
                        this.emitSubmit(validatedForm)
                            .then(() => {
                                grecaptcha.reset(recaptchaRootEl.grecaptchaId);
                                submitBtn.disabled = false;
                                gsap.to($dom.get(recaptchaSelector, validatedForm), {
                                    height: '0',
                                    onComplete() {
                                        validatedForm.repatchaShown = false
                                    }
                                });
                            })
                    }
                }
            }

            this.validatePhoneInput = field => {
                const intlTelInputInstance = intlTelInput.getInstance(field);
                const currentPhone = intlTelInputInstance.getNumber();
                const intlIsValid = intlTelInputInstance.isValidNumber();

                if (field?.intlTel.isValidNumber()) {
                    this.validator.unmark(field)
                } else {
                    this.validator.mark(field, variables.formValidationMessages.phone)
                } 
                 
                return {
                    valid: exceptionsIsExist(field)
                        ? (field.phoneExceptions.some(n => n === currentPhone) || intlIsValid)
                        : intlIsValid
                }
            };

            this.validateFormFields = formEl => {
                let {fields} = this.validator.checkAll(formEl);

                let {phone: phoneFields, other: validatedFields} = this.separateFields(fields);

                phoneFields = phoneFields.map(phoneField => {
                    const {valid} = this.validatePhoneInput(phoneField.field);
                    const formGroup = phoneField.field.closest(formGroupSelector);
                    this.checkField(formGroup);

                    const results = {
                        ...phoneField,
                        valid
                    };

                    if (valid) {
                        delete results.error;
                        $dom.removeClass(formGroup, classNames.invalid);
                    }

                    return results;
                });

                return [...validatedFields, ...phoneFields].every(({field, valid}) => {

                    if (field.tagName.toLowerCase() === 'select' && field.multiple) {
                        const currentChoice = $Page.components?.select?.fields[field?.getAttribute('id')];

                        if (currentChoice) {
                            const choiceValue = currentChoice.getValue();

                            if (Array.isArray(choiceValue)) {
                                if (choiceValue.length > 0) {
                                    this.validator.unmark(field)
                                } else {
                                    this.validator.mark(
                                        field,
                                        variables.formValidationMessages.select
                                    )
                                }

                                return choiceValue.length > 0
                            }
                            return false;
                        }

                        return false;
                    }

                    return valid
                });
            }

            this.emitSubmit = (formEl) => {
                const submitBtn = $dom.get('button[type="submit"]', formEl);
                return new Promise(resolve => {
                    const emitTm = setTimeout(() => {
                        $events.emit(customEventNames.validatedFormSubmit, {form: formEl, submitBtn}, formEl);
                        clearTimeout(emitTm);
                        resolve();
                    }, 0); // for intTelInput value providing
                })
            };

            this.initRecaptcha = () => {
                const recaptchaHolder = $dom.getAll(formSelector+' '+recaptchaSelector);
                const recaptchaPointSelector = `[${recaptchaKeyAttr}]`;

                $dom.each(recaptchaHolder, holder => {
                    if ($dom.exist(recaptchaPointSelector, holder)) {
                        const recaptchaPointEl = $dom.get(recaptchaPointSelector, holder);
                        const key = recaptchaPointEl.getAttribute(recaptchaKeyAttr);

                        holder.grecaptchaId = grecaptcha.render(recaptchaPointEl, {
                            sitekey: key,
                            callback: key => handleRecaptchaSuccess(holder, key)
                        })
                    }
                })
            }

            this.checkField = formGroup => {
                if (formGroup instanceof Event) {
                    const {target} = formGroup;

                    if (isElement(target)) formGroup = target.closest(formGroupSelector)
                }

                const field = get(formFieldSelector, formGroup);

                if (isElement(field)) {
                    if ($dom.hasClass(field, classNames.intlTelInput)) {
                        if(field.value.length === 0) {
                            this.validator.checkField(field)
                        } else {
                            checkIntlTelInput(field);
                        }
                    } else {
                        this.validator.checkField(field);
                    }
                }
            };

            this.handleForm = event => {
                const form = event.target.closest(formSelector);
                const submitBtn = $dom.get('button[type="submit"]', form);
                event.preventDefault();

                if (!this.validateFormFields(form)) {
                    event.preventDefault();
                    return
                }

                if ($dom.exist(recaptchaSelector, form)) {
                    if (!form.repatchaShown) {
                        submitBtn.disabled = true;
                        gsap.to($dom.get(recaptchaSelector, form), {
                            height: 'auto',
                            onComplete() {
                                form.repatchaShown = true;
                            }
                        })
                    }
                } else {
                    this.emitSubmit(form)
                }

            };

            this.addForm = formEl => {
                if (this.addedForms.indexOf(formEl) === -1) {
                    attr(formSelector, 'novalidate', '');
                    callAll(formGroupSelector, el => addClass(el, needValidateClassName), formEl);
                    $events.add('submit',formEl, this.handleForm);
                    this.addedForms.push(formEl);
                }
            }

            callAll(formGroupSelector, el => addClass(el, needValidateClassName));

            $events
                .delegate
                .on('submit', formSelector, this.handleForm)
                .on('input, change', formGroupSelector, this.checkField)
                .on(customEventNames.recaptchaLoaded, window, this.initRecaptcha)
            ;
        },
        onDestroy() {
            this.validator = null;

            callAll(formSelector, form => {
                form.removeAttribute('novalidate');
                if (exist(formGroupSelector, form)) callAll(formGroupSelector, el => removeClass(el, needValidateClassName), form);
                if (exist('.'+notifyClassName, form)) callAll('.'+notifyClassName, el => remove(el), form);
            });


            $events
                .delegate
                .off('submit', formSelector, this.handleForm)
                .off('change', formGroupSelector, this.checkField)
                .off(customEventNames.recaptchaLoaded, window, this.initRecaptcha)
            ;

            this.addedForms = this.addedForms.filter(formEl => {
                $events.remove('submit', formEl, this.handleForm);
                return false
            })
        },
    })
}
